/**
 * Protected March API fetch — valid Bearer token, single 401 refresh retry.
 */

import { isIamDisabled, requiresMarchAgentBearer } from './marchApiEnv';
import { isCookieAuthMode } from '../auth/cookieAuthMode';
import { handleStaleAccessOn403 } from '../auth/iamAccess';
import {
  forceRefreshAccessToken,
  getValidAccessToken,
  handleAuthFailure,
  refreshCookieSession,
  SESSION_EXPIRED_MESSAGE,
} from '../auth/tokenLifecycle';

export type FetchWithAuthOptions = RequestInit & {
  extraHeaders?: Record<string, string>;
  skip401Retry?: boolean;
  skipStaleAccessHandling?: boolean;
  onAuthRetry?: () => void;
};

async function bearerForRequest(): Promise<string> {
  // SEC-I5: in cookie mode there is no readable token to attach, and attaching
  // one would defeat the point. The browser sends the httpOnly cookie instead.
  if (isCookieAuthMode()) return '';
  if (isIamDisabled() || !requiresMarchAgentBearer()) {
    const envBearer = (import.meta.env.VITE_COMMAND_API_BEARER ?? '').toString().trim();
    return envBearer;
  }
  return await getValidAccessToken();
}

/**
 * SEC-I5: `credentials: 'include'` is what makes the browser attach the
 * httpOnly cookie on a cross-origin call (app.march.health → api.march.health).
 * Off by default, because sending credentials on every request when nothing
 * needs them widens what a mistake can leak.
 *
 * An explicit `credentials` on the call site always wins.
 */
function credentialsFor(options: RequestInit): RequestCredentials | undefined {
  if (options.credentials) return options.credentials;
  return isCookieAuthMode() ? 'include' : undefined;
}

function mergeHeaders(
  base: RequestInit['headers'],
  extra: Record<string, string>,
  bearer: string,
): Headers {
  const headers = new Headers(base);
  for (const [key, value] of Object.entries(extra)) {
    headers.set(key, value);
  }
  if (bearer) {
    headers.set('Authorization', `Bearer ${bearer}`);
  }
  return headers;
}

/** Fetch with Authorization Bearer from centralized token lifecycle. */
export async function fetchWithAuth(
  url: string,
  options: FetchWithAuthOptions = {},
): Promise<Response> {
  const {
    extraHeaders = {},
    skip401Retry = false,
    skipStaleAccessHandling = false,
    onAuthRetry,
    ...fetchOptions
  } = options;
  const bearer = await bearerForRequest();
  // In cookie mode an empty bearer is the expected state, not a dead session —
  // the credential is a cookie this code cannot see. Only the header-based
  // modes can conclude "no token means signed out".
  if (!isCookieAuthMode() && requiresMarchAgentBearer() && !isIamDisabled() && !bearer) {
    throw new Error(SESSION_EXPIRED_MESSAGE);
  }

  const credentials = credentialsFor(fetchOptions);
  const headers = mergeHeaders(fetchOptions.headers, extraHeaders, bearer);
  let response = await fetch(url, { ...fetchOptions, credentials, headers });

  if (response.status === 401 && !skip401Retry && !isIamDisabled()) {
    // SEC-I5: cookie mode refreshes server-side and gets no token back, so the
    // retry re-sends the request unchanged and lets the NEW cookie carry it.
    // Reusing the header path here would look like it worked and always fail:
    // `forceRefreshAccessToken` returns a token string, and in cookie mode
    // there is never one to return.
    if (isCookieAuthMode()) {
      onAuthRetry?.();
      const refreshed = await refreshCookieSession();
      if (refreshed) {
        response = await fetch(url, { ...fetchOptions, credentials, headers });
      }
      if (response.status === 401) {
        handleAuthFailure();
      }
    } else if (requiresMarchAgentBearer()) {
      onAuthRetry?.();
      const refreshed = await forceRefreshAccessToken();
      if (refreshed) {
        const retryHeaders = mergeHeaders(fetchOptions.headers, extraHeaders, refreshed);
        response = await fetch(url, { ...fetchOptions, headers: retryHeaders });
      }
      if (response.status === 401) {
        handleAuthFailure();
      }
    }
  }

  if (
    response.status === 403 &&
    !skipStaleAccessHandling &&
    requiresMarchAgentBearer() &&
    !isIamDisabled()
  ) {
    await handleStaleAccessOn403();
  }

  return response;
}
